import React, { useState } from "react";
import {
  Box,
  TextField,
  Chip,
  ListItem,
  ListItemText,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

interface Option {
  id: string;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: Option[];
  selectedValues: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder?: string;
  width?: string | number;
  maxHeight?: string | number;
}

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: "2px",
  color: theme.palette.primary.main,
  border: "1px solid",
  backgroundColor: "white",
  borderColor: theme.palette.primary.main,
  "& .MuiChip-deleteIcon": {
    color: theme.palette.primary.light,
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
}));

export default function MultiSelectDropdown({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  width = "100%",
  maxHeight = 300,
}: MultiSelectDropdownProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOptionClick = (optionId: string) => {
    const newSelectedValues = selectedValues.includes(optionId)
      ? selectedValues.filter((id) => id !== optionId)
      : [...selectedValues, optionId];
    onChange(newSelectedValues);
  };

  const handleChipDelete = (optionId: string) => {
    onChange(selectedValues.filter((id) => id !== optionId));
  };

  return (
    <Box sx={{ width }}>
      <FormControl fullWidth>
        <InputLabel id={`${label}-label`}>{label}</InputLabel>
        <Select
          labelId={`${label}-label`}
          multiple
          value={selectedValues}
          displayEmpty
          input={<StyledOutlinedInput label={label} />}
          renderValue={(selected) => (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                maxHeight: "48px",
                overflow: "hidden",
                alignItems: "center",
              }}
            >
              {!selected || selected.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                    py: 1,
                  }}
                >
                  {placeholder}
                </Typography>
              ) : selected.length > 3 ? (
                <>
                  {selected.slice(0, 3).map((value) => {
                    const option = options.find((opt) => opt.id === value);
                    return (
                      <StyledChip
                        key={value}
                        label={option ? option.label : value}
                        onDelete={() => handleChipDelete(value)}
                        size="small"
                      />
                    );
                  })}
                  <StyledChip
                    label={`+${selected.length - 3} more`}
                    size="small"
                    sx={{
                      backgroundColor: "primary.light",
                      color: "white",
                      "& .MuiChip-deleteIcon": {
                        display: "none",
                      },
                    }}
                  />
                </>
              ) : (
                selected.map((value) => {
                  const option = options.find((opt) => opt.id === value);
                  return (
                    <StyledChip
                      key={value}
                      label={option ? option.label : value}
                      onDelete={() => handleChipDelete(value)}
                      size="small"
                    />
                  );
                })
              )}
            </Box>
          )}
          sx={{
            minHeight: "56px",
            "& .MuiSelect-select": {
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
            },
          }}
          MenuProps={{
            PaperProps: {
              style: { maxHeight },
            },
          }}
        >
          <Box sx={{ p: 1 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ mb: 1 }}
            />
            {filteredOptions.length === 0 ? (
              <ListItem>
                <ListItemText primary="No options found" />
              </ListItem>
            ) : (
              filteredOptions.map((option) => (
                <MenuItem
                  key={option.id}
                  onClick={() => handleOptionClick(option.id)}
                  dense
                  sx={{ py: 0.5 }}
                >
                  <Checkbox
                    checked={selectedValues.includes(option.id)}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </MenuItem>
              ))
            )}
          </Box>
        </Select>
      </FormControl>
    </Box>
  );
}
